import { fetchDataFromAPITyped, ApiRequest, ApiResponse } from './api-requests';

export interface PartnerTermsVersion {
    versionId: string;
    agreementType: string;
    version: string;
    title: string;
    content: string;
    contentHtml?: string;
    documentHash: string;
    effectiveDate: string;
    expirationDate?: string;
    isCurrent: boolean;
    createdAt: string;
}

export interface PartnerTermsStatus {
    hasAgreement: boolean;
    requiresAcceptance: boolean;
    currentVersion?: string;
    acceptedVersion?: string;
    acceptedAt?: string;
    needsUpdate?: boolean;
    documentText?: string; // The actual text of the terms document
}

export interface PartnerTermsAcceptance {
    agreementId: string;
    agreementVersion: string;
    acceptedAt: string;
    status: string;
    legalName?: string;
    businessEntity?: string;
    taxId?: string;
}

export interface AcceptTermsPayload {
    versionId: string;
    agreementType: string;
    legalName?: string;
    businessEntity?: string;
    taxId?: string;
}

export interface AcceptTermsResponse {
    message: string;
    agreementId: string;
    agreementVersion: string;
    acceptedAt: string;
    status: string;
}

/**
 * Get the current version of partner terms
 */
export async function getCurrentPartnerTerms(agreementType: string = 'SPLIT_PARTNER_TERMS') {
    const apiRequest: ApiRequest = {
        route: `partner/terms/current?type=${agreementType}`,
        request: {
            method: 'GET',
            entity: 'partner-terms',
            body: null,
        },
    };
    return await fetchDataFromAPITyped<PartnerTermsVersion>(apiRequest);
}

/**
 * Check if user has accepted partner terms
 */
export async function getPartnerTermsStatus(agreementType: string = 'SPLIT_PARTNER_TERMS') {
    const apiRequest: ApiRequest = {
        route: `partner/terms/status?type=${agreementType}`,
        request: {
            method: 'GET',
            entity: 'partner-terms',
            body: null,
        },
    };
    return await fetchDataFromAPITyped<PartnerTermsStatus>(apiRequest);
}

/**
 * Get user's accepted agreement details
 */
export async function getMyPartnerAgreement(agreementType: string = 'SPLIT_PARTNER_TERMS') {
    const apiRequest: ApiRequest = {
        route: `partner/terms/my-agreement?type=${agreementType}`,
        request: {
            method: 'GET',
            entity: 'partner-terms',
            body: null,
        },
    };
    return await fetchDataFromAPITyped<PartnerTermsAcceptance>(apiRequest);
}

/**
 * Accept partner terms and conditions
 */
export async function acceptPartnerTerms(payload: AcceptTermsPayload) {
    const apiRequest: ApiRequest = {
        route: 'partner/terms/accept',
        request: {
            method: 'POST',
            entity: 'partner-terms',
            body: payload,
        },
    };
    return await fetchDataFromAPITyped<AcceptTermsResponse>(apiRequest);
}

/**
 * Get pending partnership associations (chargers awaiting terms acceptance)
 */
export async function getPendingPartnerships() {
    const apiRequest: ApiRequest = {
        route: 'charger/partner/getPartnerChargers',
        request: {
            method: 'GET',
            entity: 'partner-chargers',
            body: null,
        },
    };
    
    const response = await fetchDataFromAPITyped<any>(apiRequest);
    
    if (response.status === 200 && response.payload) {
        const chargers = response.payload?.chargers || [];
        // Filter for chargers with split permission
        const pending = chargers.filter((charger: any) => {
            const permissions = charger.permissions || [];
            return permissions.includes('split');
        });
        return { status: 200, payload: { chargers: pending } };
    }
    
    return response;
}
